/**
 * Package Response Patch Utility
 *
 * This utility patches backend response values for packages API
 * until backend fixes are implemented.
 *
 * Patches:
 * - Color values (background_color, title_color, th_color)
 * - Recommended flag (is_recommended)
 * - Text values (name, description, features_list, features)
 */

import { Package } from '@/types/package-type';
import { useTranslations } from 'next-intl';

/**
 * Feature patch configuration for nested features
 */
interface FeaturePatch {
  slug: string; // Identifier for matching existing feature
  name?: string;
  description?: string | null;
}

/**
 * Patch configuration for packages
 * Key: Package ID (string)
 * Value: Fields to override in package object
 */
interface PackagePatchConfig {
  [packageId: string]: {
    // Color fields
    background_color?: string;
    title_color?: string;
    th_color?: string;
    is_recommended?: boolean;

    // Text fields
    name?: string;
    description?: string;
    features_list?: string[]; // Full replacement of features list
    features?: FeaturePatch[]; // Selective patch of nested features by slug
  };
}

/**
 * Patch configuration function
 * Returns patch config with internationalized text values
 *
 * Text fields (name, description, features_list, features) are internationalized
 * using translation keys. Color and flag values remain hard-coded.
 *
 * @param t - Translation function from useTranslations('packages')
 * @returns Package patch configuration
 */
function getPackagePatchConfig(
  t: ReturnType<typeof useTranslations<'packages'>>
): PackagePatchConfig {
  return {
  // Admission Packages
  '1': { // Free (basic-package)
    background_color: 'white',
    title_color: '#3DCC9C',
    th_color: '#3DCC9C',
    is_recommended: false,
    name: t('patch.1.name'),
    description: t('patch.1.description'),
    features_list: [
      t('patch.1.features_list.0'),
      t('patch.1.features_list.1'),
      t('patch.1.features_list.2'),
      t('patch.1.features_list.3'),
    ],
    features: [
      {
        slug: 'applies',
        name: t('patch.1.features.applies.name'),
        description: t('patch.1.features.applies.description') || null,
      },
    ],
  },
  '2': { // Premium (standard-package)
    background_color: 'white',
    title_color: '#ff734d',
    th_color: '#ff734d',
    is_recommended: false,
    name: t('patch.2.name'),
    description: t('patch.2.description'),
    features_list: [
      t('patch.2.features_list.0'),
      t('patch.2.features_list.1'),
      t('patch.2.features_list.2'),
      t('patch.2.features_list.3'),
      t('patch.2.features_list.4'),
    ],
    features: [
      {
        slug: 'applies',
        name: t('patch.2.features.applies.name'),
        description: t('patch.2.features.applies.description') || null,
      },
    ],
  },
  '3': { // Pro (pro-package)
    background_color: 'primary',
    title_color: '#ffffff',
    th_color: '#704DFF',
    is_recommended: true,
    name: t('patch.3.name'),
    description: t('patch.3.description'),
    features_list: [
      t('patch.3.features_list.0'),
      t('patch.3.features_list.1'),
      t('patch.3.features_list.2'),
      t('patch.3.features_list.3'),
      t('patch.3.features_list.4'),
    ],
    features: [
      {
        slug: 'applies',
        name: t('patch.3.features.applies.name'),
        description: t('patch.3.features.applies.description') || null,
      },
    ],
  },
  // Funding Packages
  '7': { // Free Trial (trial)
    background_color: 'white',
    title_color: '#3DCC9C',
    th_color: '#3DCC9C',
    is_recommended: false,
    name: t('patch.7.name'),
    description: t('patch.7.description'),
    features_list: [
      t('patch.7.features_list.0'),
      t('patch.7.features_list.1'),
      t('patch.7.features_list.2'),
      t('patch.7.features_list.3'),
    ],
    features: [
      {
        slug: 'email_preview',
        name: t('patch.7.features.email_preview.name'),
        description: t('patch.7.features.email_preview.description'),
      },
    ],
  },
  '8': { // Basic (normal)
    background_color: 'white',
    title_color: '#ff734d',
    th_color: '#ff734d',
    is_recommended: false,
    name: t('patch.8.name'),
    // استفاده از subpackages.100 به عنوان fallback برای backward compatibility
    description: t('patch.8.subpackages.100.description'),
    features_list: [
      t('patch.8.subpackages.100.features_list.0'),
      t('patch.8.subpackages.100.features_list.1'),
      t('patch.8.subpackages.100.features_list.2'),
      t('patch.8.subpackages.100.features_list.3'),
      t('patch.8.subpackages.100.features_list.4'),
    ],
    features: [
      {
        slug: 'send_emails',
        name: t('patch.8.features.send_emails.name'),
        description: t('patch.8.features.send_emails.description'),
      },
      {
        slug: 'email_preview',
        name: t('patch.8.features.email_preview.name'),
        description: t('patch.8.features.email_preview.description'),
      },
    ],
  },
  '9': { // Advisor (advisor)
    background_color: 'primary',
    title_color: '#ffffff',
    th_color: '#704DFF',
    is_recommended: true,
    name: t('patch.9.name'),
    description: t('patch.9.description'),
    features_list: [
      t('patch.9.features_list.0'),
      t('patch.9.features_list.1'),
      t('patch.9.features_list.2'),
      t('patch.9.features_list.3'),
      t('patch.9.features_list.4'),
    ],
    features: [
      {
        slug: 'send_emails',
        name: t('patch.9.features.send_emails.name'),
        description: t('patch.9.features.send_emails.description'),
      },
      {
        slug: 'email_preview',
        name: t('patch.9.features.email_preview.name'),
        description: t('patch.9.features.email_preview.description'),
      },
      {
        slug: 'agent',
        name: t('patch.9.features.agent.name'),
        description: t('patch.9.features.agent.description'),
      },
    ],
  },
  };
}

/**
 * API Response structure from backend
 */
interface PackagesApiResponse {
  success: boolean;
  message: string;
  data: Package[];
  meta?: unknown;
}

/**
 * Patches package response by applying patch config to each package
 *
 * @param response - Original API response from backend
 * @param t - Translation function from useTranslations('packages')
 * @returns Patched response with same structure
 */
export function patchPackagesResponse(
  response: PackagesApiResponse,
  t: ReturnType<typeof useTranslations<'packages'>>
): PackagesApiResponse {
  // Validate response structure
  if (!response || !response.success || !Array.isArray(response.data)) {
    console.warn('[PackagePatch] Invalid response structure, skipping patch');
    return response;
  }

  // Get internationalized patch config
  const config = getPackagePatchConfig(t);

  // Deep clone to avoid mutation (including nested arrays)
  const patchedResponse: PackagesApiResponse = {
    ...response,
    data: response.data.map((pkg) => ({
      ...pkg,
      features_list: pkg.features_list ? [...pkg.features_list] : undefined,
      features: pkg.features ? pkg.features.map((f) => ({ ...f })) : undefined,
      comparison_services: pkg.comparison_services ? { ...pkg.comparison_services } : undefined,
    })),
  };

  let patchedCount = 0;
  const patchLog: string[] = [];

  // Apply patches to each package
  patchedResponse.data.forEach((pkg) => {
    const packageId = String(pkg.id);
    const patchConfig = config[packageId];

    if (patchConfig) {
      // Log patch operation
      const patchFields: string[] = [];

      // Apply patch fields
      if (patchConfig.background_color !== undefined) {
        const oldValue = pkg.background_color;
        pkg.background_color = patchConfig.background_color;
        patchFields.push(`background_color: "${oldValue}" → "${patchConfig.background_color}"`);
      }

      if (patchConfig.title_color !== undefined) {
        const oldValue = pkg.title_color;
        pkg.title_color = patchConfig.title_color;
        patchFields.push(`title_color: "${oldValue}" → "${patchConfig.title_color}"`);
      }

      if (patchConfig.th_color !== undefined) {
        const oldValue = pkg.th_color;
        pkg.th_color = patchConfig.th_color;
        patchFields.push(`th_color: "${oldValue}" → "${patchConfig.th_color}"`);
      }

      if (patchConfig.is_recommended !== undefined) {
        const oldValue = pkg.is_recommended;
        pkg.is_recommended = patchConfig.is_recommended;
        patchFields.push(`is_recommended: ${oldValue} → ${patchConfig.is_recommended}`);
      }

      // Apply text field patches
      if (patchConfig.name !== undefined) {
        // Validate name
        if (typeof patchConfig.name === 'string' && patchConfig.name.trim().length > 0) {
          const oldValue = pkg.name;
          pkg.name = patchConfig.name;
          patchFields.push(`name: "${oldValue}" → "${patchConfig.name}"`);
        } else {
          console.warn(`[PackagePatch] Invalid name for package ID ${packageId}, skipping`);
        }
      }

      if (patchConfig.description !== undefined) {
        // Validate description
        if (typeof patchConfig.description === 'string') {
          const oldLength = pkg.description?.length || 0;
          pkg.description = patchConfig.description;
          patchFields.push(`description: updated (${oldLength} → ${patchConfig.description.length} chars)`);
        } else {
          console.warn(`[PackagePatch] Invalid description for package ID ${packageId}, skipping`);
        }
      }

      if (patchConfig.features_list !== undefined) {
        // Validate features_list
        if (Array.isArray(patchConfig.features_list)) {
          const oldCount = pkg.features_list?.length || 0;
          pkg.features_list = [...patchConfig.features_list];
          patchFields.push(`features_list: ${oldCount} → ${patchConfig.features_list.length} items`);
        } else {
          console.warn(`[PackagePatch] Invalid features_list for package ID ${packageId}, skipping`);
        }
      }

      // Apply nested features patches
      if (patchConfig.features && Array.isArray(patchConfig.features)) {
        const featurePatches: string[] = [];

        patchConfig.features.forEach((patchFeature) => {
          // Validate patch feature has slug
          if (!patchFeature.slug || typeof patchFeature.slug !== 'string') {
            console.warn(`[PackagePatch] Invalid feature patch (missing slug) for package ID ${packageId}, skipping`);
            return;
          }

          // Find matching feature by slug
          const existingFeature = pkg.features?.find((f) => f.slug === patchFeature.slug);

          if (existingFeature) {
            // Apply patch to matching feature
            if (patchFeature.name !== undefined) {
              if (typeof patchFeature.name === 'string' && patchFeature.name.trim().length > 0) {
                const oldValue = existingFeature.name;
                existingFeature.name = patchFeature.name;
                featurePatches.push(`${patchFeature.slug}.name: "${oldValue}" → "${patchFeature.name}"`);
              }
            }

            if (patchFeature.description !== undefined) {
              // Allow null for description
              const oldValue = existingFeature.description;
              existingFeature.description = patchFeature.description;
              if (patchFeature.description === null) {
                featurePatches.push(`${patchFeature.slug}.description: "${oldValue}" → null`);
              } else {
                featurePatches.push(`${patchFeature.slug}.description: updated`);
              }
            }
          } else {
            console.warn(
              `[PackagePatch] Feature with slug "${patchFeature.slug}" not found in package ID ${packageId}, skipping`
            );
          }
        });

        if (featurePatches.length > 0) {
          patchFields.push(`features: ${featurePatches.join(', ')}`);
        }
      }

      if (patchFields.length > 0) {
        patchedCount++;
        patchLog.push(
          `[PackagePatch] Package ID ${packageId} (${pkg.name}): ${patchFields.join(', ')}`
        );
      }
    }
  });

  // Log summary
  if (patchedCount > 0) {
    console.log(`[PackagePatch] Applied patches to ${patchedCount} package(s):`);
    patchLog.forEach((log) => console.log(log));
  } else {
    console.log('[PackagePatch] No patches applied (no matching package IDs found)');
  }

  return patchedResponse;
}

